// Rep screens: S15 dashboard, S16 period, S17 history, S18 unpaid
import React, { useState as useStateRep, useEffect as useEffectRep, useMemo as useMemoRep } from 'react';
import { Icon, Pill, Button, Card, Money, Banner, Section, Select, Input } from './ui.jsx';
import { REP_HISTORY, sumBy } from './data.jsx';
import { ExportMenu } from './reports.jsx';
import { api } from './api.js';

// Adapter: DB rows use snake_case fields; rep UI code was written for the
// legacy camelCase shape (`i.gp`, `i.repId`, `i.date`, `i.doc`, `i.client`).
// adaptInvoice returns a merged object with both so existing JSX Just Works.
//
// Approver rejection (migration 0015): if the approver rejected this
// invoice, the rep earns $0 on it. We zero out commission for sums but
// keep the calculated number visible in `preRejectionCommission` so we
// can show a strikethrough.
function adaptInvoice(i, customerById) {
  // For cash-basis held invoices, derive the would-be commission from
  // net × rate so the rep can see what they'll earn when the invoice is paid.
  const heldWouldBe = i.heldForCollection
    ? Math.round((Number(i.net) || 0) * (Number(i.rate) || 0) * 100) / 100
    : null;
  // Resolve the client display name from the customer row when available,
  // else fall back to the entity (staffing supplier) or branch name.
  const custName = customerById && i.customerId
    ? customerById.get(i.customerId)?.name
    : null;
  return {
    ...i,
    gp: i.grossProfit,
    repId: i.repUserId,
    date: i.invoiceDate,
    doc: i.branch + '/' + i.invoiceNumber,
    client: custName || i.entity || i.branch,
    clientId: i.customerId,
    preRejectionCommission: i.commission,
    commission: i.rejected ? 0 : i.commission,
    heldWouldBeCommission: heldWouldBe,
  };
}

// Quick rep-level aggregator (runs on already-filtered invoices).
function summarize(invoices) {
  const rows = invoices.filter(i => !i.excluded);
  return {
    count: rows.length,
    totalBill:  rows.reduce((a,i) => a + i.totalBill, 0),
    gp:         rows.reduce((a,i) => a + i.gp, 0),
    commission: rows.reduce((a,i) => a + i.commission, 0),
    unpaid:     rows.filter(i => !i.paid).reduce((a,i) => a + i.outstanding, 0),
  };
}

export function ScreenRepDashboard({ rep, onOpenInvoice, tenant, period, currentUser }) {
  const [run, setRun]       = useStateRep(null);
  const [allRuns, setAllRuns] = useStateRep([]); // for the run-picker dropdown
  const [selectedRunId, setSelectedRunId] = useStateRep(null);
  const [invoices, setInvoices] = useStateRep([]);
  const [loading, setLoading]   = useStateRep(true);
  const [previewRep, setPreviewRep] = useStateRep(null);
  const [plans, setPlans]   = useStateRep([]); // for showing the rep's plan rates
  const [customers, setCustomers] = useStateRep([]); // for resolving client name
  // Bulk-selection state for the main invoice table. Declared at the top
  // of the component (before any early-return guards) so hook order stays
  // consistent across renders — otherwise React error #310.
  const [selectedIds, setSelectedIds] = useStateRep(new Set());
  const [bulkBusy, setBulkBusy]       = useStateRep(false);

  // "View as rep" preview mode: ?as=<userId> in the URL.
  // When set + current user is admin, the dashboard loads as if THAT rep
  // were signed in instead of you. Useful for admins staging reps before
  // sending invitations.
  const asParam = (() => {
    try { return new URLSearchParams(window.location.search).get('as'); }
    catch { return null; }
  })();
  const isPreview = !!asParam && currentUser?.role === 'admin';

  // Initial load: fetch ALL runs (latest first), let the user switch via
  // a dropdown. Default selection = most recent.
  useEffectRep(() => {
    let alive = true;
    (async () => {
      try {
        const [allRunsRaw, plansList, custList] = await Promise.all([
          api.runs.list(),
          api.commissionPlans.list().catch(() => []),
          api.customers.list().catch(() => []),
        ]);
        // Filter out runs hidden from reps (e.g. import-only carryover
        // sources like the March 2026 import). Admins viewing as a rep
        // also respect this — the rep dashboard is a rep's-eye view, so
        // it should match what a real signed-in rep would see.
        const runs = allRunsRaw.filter(r => r.visibleToReps !== false);
        if (!runs.length) { if (alive) setLoading(false); return; }
        if (alive) {
          setAllRuns(runs);
          setPlans(plansList);
          setCustomers(custList);
          // Honor whatever's already selected, else most recent.
          setSelectedRunId(prev => prev || runs[0].id);
        }
        if (isPreview) {
          const users = await api.users.list();
          const target = users.find(u => u.id === asParam);
          if (target && alive) setPreviewRep(target);
        }
      } catch (err) { console.error('Rep dashboard initial load:', err); }
    })();
    return () => { alive = false; };
  }, [asParam]);

  // Whenever the selected run changes (or after the initial set), fetch
  // its invoices. Separate effect so the dropdown swaps without re-running
  // the users lookup.
  useEffectRep(() => {
    if (!selectedRunId) return;
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const target = allRuns.find(r => r.id === selectedRunId);
        if (!target) return;
        let invs;
        if (isPreview && asParam) {
          invs = await api.invoices.listForRep(target.id, asParam);
        } else {
          invs = await api.invoices.listByRun(target.id);
        }
        if (!alive) return;
        const customerById = new Map(customers.map(c => [c.id, c]));
        setRun(target);
        setInvoices(invs.map(i => adaptInvoice(i, customerById)));
      } catch (err) { console.error('Rep dashboard run load:', err); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [selectedRunId, isPreview, asParam, allRuns.length, customers.length]);

  if (loading) return <div className="p-10 text-center text-neutral-500 text-[13px]">Loading your commission…</div>;
  if (!run)   return <Banner tone="info" icon="info" title="No runs yet">Your finance team hasn't started a commission run.</Banner>;

  const repInvoices = invoices; // already RLS-scoped to self
  const t = summarize(repInvoices);
  const totalACA   = sumBy(repInvoices, i => i.aca);
  const totalSICK  = sumBy(repInvoices, i => i.sick);
  const totalWages = sumBy(repInvoices, i => i.grossWages);
  const finalized = run.status === 'finalized';
  const approved  = run.status === 'approved';

  // Resolve THIS rep's plan rates (preferred over the legacy tenant.rates
  // snapshot used in earlier versions of the dashboard). When the rep
  // hasn't been assigned a specific plan, fall back to the tenant's
  // default plan.
  const effRep = rep || previewRep;
  const effectivePlan = (effRep?.commissionPlanId && plans.find(p => p.id === effRep.commissionPlanId))
                     || plans.find(p => p.isDefault)
                     || null;
  const planACA  = effectivePlan?.deductions?.find(d => /aca/i.test(d.label || ''))?.rate;
  const planSICK = effectivePlan?.deductions?.find(d => /sick/i.test(d.label || ''))?.rate;
  const planY1   = effectivePlan?.tiers?.find(t => /^y1$/i.test(t.label || ''))?.rate;
  const planY2   = effectivePlan?.tiers?.find(t => /^y2\+?$/i.test(t.label || ''))?.rate;

  // Effective ACA / SICK % the rep WAS charged on these invoices
  // (sum of stored values / total wages). If this is different from the
  // plan's configured rate, the run was last calculated before the plan
  // was edited — Recalculate the run to refresh.
  const appliedACA  = totalWages > 0 ? totalACA  / totalWages : null;
  const appliedSICK = totalWages > 0 ? totalSICK / totalWages : null;

  // Compute the effective Y1/Y2 rates that were actually stamped on
  // invoices (commission / net per invoice, weighted by net). When the
  // invoice was calculated with a stale plan rate, this won't match the
  // plan-configured rate and we show a "stale" banner.
  const y1Invs = repInvoices.filter(i => i.tier === 'y1' && !i.rejected && !i.heldForCollection);
  const y2Invs = repInvoices.filter(i => i.tier === 'y2' && !i.rejected && !i.heldForCollection);
  const sumY1Net = sumBy(y1Invs, i => i.net);
  const sumY2Net = sumBy(y2Invs, i => i.net);
  const appliedY1 = sumY1Net > 0 ? sumBy(y1Invs, i => i.commission) / sumY1Net : null;
  const appliedY2 = sumY2Net > 0 ? sumBy(y2Invs, i => i.commission) / sumY2Net : null;

  // Flag when the math behind the totals doesn't match the plan's
  // current configuration — typically because the run was calculated
  // before someone edited the plan. Recalculate fixes it.
  const drift = (a, b) => a != null && b != null && Math.abs(a - b) > 0.0001;
  const isStaleACA  = drift(appliedACA, planACA);
  const isStaleSICK = drift(appliedSICK, planSICK);
  const isStaleY1   = drift(appliedY1, planY1);
  const isStaleY2   = drift(appliedY2, planY2);
  const isStale = isStaleACA || isStaleSICK || isStaleY1 || isStaleY2;

  const carriedForward = repInvoices.filter(i => !i.paid && i.ageDays > 30);
  const overdue90      = repInvoices.filter(i => !i.paid && i.ageDays > 90);
  const carriedTotal   = sumBy(carriedForward, i => i.outstanding);
  const overdueTotal   = sumBy(overdue90, i => i.outstanding);

  // "Possible commission carry forward" — what the rep WOULD earn on each
  // carried-forward invoice if/when the customer pays. For invoices held
  // under cash basis, this is the would-be commission already computed
  // (heldWouldBeCommission). For accrual-basis invoices, commission was
  // already paid on bill, so nothing carries over from a payment standpoint.
  const carryCommissionFor = (inv) => {
    if (inv.heldForCollection) {
      return Number(inv.heldWouldBeCommission || 0);
    }
    // Accrual: commission was already booked. No carry on payment.
    return 0;
  };
  const possibleCarryTotal = sumBy(carriedForward, carryCommissionFor);
  const hasAnyHeld = carriedForward.some(i => i.heldForCollection);

  // Effective period label for the page header + ExportMenu — falls back
  // to the run.period column (e.g. "March 2026" or a custom range).
  const periodLabel = run.period || period;

  // Admin-only invoice exclude/include from within preview mode. Reps
  // don't get this affordance — only admins viewing as a rep on a
  // draft run. Updates DB + refreshes local state so the drawer reflects
  // the change without needing a page reload.
  const canEditInvoices = isPreview && run.status === 'draft';

  const toggleSelected = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelected = () => setSelectedIds(new Set());
  // Bulk apply excluded=value to every selected invoice.
  const bulkSetExcluded = async (value) => {
    if (selectedIds.size === 0) return;
    setBulkBusy(true);
    try {
      const ids = Array.from(selectedIds);
      const updated = await Promise.all(ids.map(id =>
        api.invoices.update(id, { excluded: !!value })
      ));
      const customerById = new Map(customers.map(c => [c.id, c]));
      setInvoices(prev => prev.map(i => {
        const u = updated.find(x => x.id === i.id);
        return u ? adaptInvoice(u, customerById) : i;
      }));
      clearSelected();
    } catch (err) {
      alert('Bulk update failed: ' + (err.message || err));
    } finally { setBulkBusy(false); }
  };
  const toggleExcludeInvoice = async (invoiceId, makeExcluded) => {
    const updated = await api.invoices.update(invoiceId, { excluded: !!makeExcluded });
    setInvoices(prev => {
      const customerById = new Map(customers.map(c => [c.id, c]));
      return prev.map(i => i.id === invoiceId ? adaptInvoice(updated, customerById) : i);
    });
  };
  // Helper: enrich an invoice payload with admin-action context the
  // drawer needs (Override / Exclude / Reject). The 3 click sites in
  // this file all use it so they stay consistent.
  const enrich = (inv) => ({
    ...inv,
    _canExclude: canEditInvoices,
    _onToggleExclude: toggleExcludeInvoice,
  });

  return (
    <div className="space-y-7">
      {/* Run picker — present in both admin preview and own-view (a rep
          can review prior months too). Only shown when there's more than
          one run; otherwise pointless clutter. */}
      {allRuns.length > 1 && (
        <div className="flex items-center justify-between gap-3 -mb-3">
          <div className="flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-[0.06em] font-semibold text-neutral-500">Commission run</span>
            <select
              value={selectedRunId || ''}
              onChange={e => setSelectedRunId(e.target.value)}
              className="h-9 px-3 text-[13px] rounded-md border border-neutral-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700">
              {allRuns.map(r => (
                <option key={r.id} value={r.id}>
                  {r.period}{r.status !== 'approved' ? ` · ${r.status}` : ''}
                </option>
              ))}
            </select>
          </div>
          <ExportMenu rep={rep || previewRep} invoices={invoices} period={periodLabel} tenant={tenant}/>
        </div>
      )}

      {isStale && (
        <Banner tone="warn" icon="warn" title="Numbers below were calculated against an older plan">
          The plan rates have been edited since this run was last calculated, so the dollar amounts
          may not match the plan rates shown below.{' '}
          {isStaleY2 && planY2 != null && appliedY2 != null && (
            <span className="font-mono">Y2+: applied {(appliedY2 * 100).toFixed(2)}%, plan now {(planY2 * 100).toFixed(2)}%.{' '}</span>
          )}
          {isStaleY1 && planY1 != null && appliedY1 != null && (
            <span className="font-mono">Y1: applied {(appliedY1 * 100).toFixed(2)}%, plan now {(planY1 * 100).toFixed(2)}%.{' '}</span>
          )}
          {isStaleACA && planACA != null && appliedACA != null && (
            <span className="font-mono">ACA: applied {(appliedACA * 100).toFixed(2)}%, plan now {(planACA * 100).toFixed(2)}%.{' '}</span>
          )}
          {isStaleSICK && planSICK != null && appliedSICK != null && (
            <span className="font-mono">SICK: applied {(appliedSICK * 100).toFixed(2)}%, plan now {(planSICK * 100).toFixed(2)}%.{' '}</span>
          )}
          Open this run in Commission runs and click <b>Recalculate</b> to refresh.
        </Banner>
      )}
      {isPreview && previewRep && (
        <Banner tone="warn" icon="info" title={`Admin preview — viewing as ${previewRep.name}`}>
          You're seeing the dashboard exactly as <b>{previewRep.name}</b> ({previewRep.email}) would see it after signing in.
          {previewRep.staged && <> They're currently <b>staged</b> — no login created yet.</>}
          {' '}
          <button onClick={() => { window.history.replaceState({}, '', '/users'); window.dispatchEvent(new PopStateEvent('popstate')); }}
            className="underline font-semibold">
            ← Exit preview
          </button>
        </Banner>
      )}
      {approved ? (
        <Banner tone="success" icon="check" title={`Approved on ${run.approvedAt?.slice(0, 10) || 'recent'}`}>
          Your commission for {run.period} has been approved. Payroll runs next.
        </Banner>
      ) : finalized ? (
        <Banner tone="info" icon="lock" title="Finalized — pending approval">
          Your commission for {run.period} is locked and awaiting approver sign-off. Numbers here will not change; approval is typically granted within 3 business days.
        </Banner>
      ) : (
        <Banner tone="warn" icon="info" title="This period is still being calculated">
          Numbers may change until Finance finalizes the run. Final numbers are typically ready within 5 business days after month-end.
        </Banner>
      )}

      <div>
        <div className="text-[12px] font-semibold uppercase tracking-[0.08em] text-neutral-500">{periodLabel || 'This month'}</div>
        <div className="mt-1 flex items-baseline gap-4">
          <div className="text-[56px] font-bold font-mono tabular-nums text-neutral-900 tracking-[-0.02em] leading-none">
            ${t.commission.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>
        <div className="text-[13px] text-neutral-600 mt-2">from {t.count} invoices across {new Set(repInvoices.map(i => i.clientId)).size} clients</div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card padding="p-5">
          <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500">Total Bill</div>
          <div className="mt-1 text-[22px] font-bold font-mono tabular-nums text-neutral-900 leading-none">${t.totalBill.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
          <div className="text-[12px] text-neutral-500 mt-1.5">Billed to your clients</div>
        </Card>
        <Card padding="p-5">
          <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500">Gross Profit</div>
          <div className="mt-1 text-[22px] font-bold font-mono tabular-nums text-neutral-900 leading-none">${t.gp.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
          <div className="text-[12px] text-neutral-500 mt-1.5">Before ACA &amp; SICK</div>
        </Card>
        <Card padding="p-5" className={t.unpaid > 0 ? 'border-amber-300' : ''}>
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500">Unpaid</div>
            {t.unpaid > 0 && <Pill tone="warn">action</Pill>}
          </div>
          <div className="mt-1 text-[22px] font-bold font-mono tabular-nums text-amber-700 leading-none">${t.unpaid.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
          <div className="text-[12px] text-neutral-500 mt-1.5">{repInvoices.filter(i => !i.paid).length} invoices outstanding</div>
        </Card>
      </div>

      <Section title="How we got there" sub="Every line traces back to your invoices. Click any invoice below for the full calculation.">
        <Card padding="p-0">
          <div className="divide-y divide-neutral-200">
            <BreakdownRow label="Gross Profit from your invoices" value={t.gp}/>
            <BreakdownRow label={`minus ACA (${((planACA ?? appliedACA ?? 0.018) * 100).toFixed(2)}%)`}   value={-totalACA}  sub/>
            <BreakdownRow label={`minus SICK (${((planSICK ?? appliedSICK ?? 0.012) * 100).toFixed(2)}%)`} value={-totalSICK} sub/>
            <BreakdownRow label="= NET" value={t.gp - totalACA - totalSICK} bold/>
            <BreakdownRow label={`× Your rate (Y1 · ${(((effRep?.y1Rate ?? planY1 ?? 0.05)) * 100).toFixed(2)}% new clients / Y2+ · ${(((effRep?.y2Rate ?? planY2 ?? 0.03)) * 100).toFixed(2)}% returning)`} value={null} rate/>
            <div className="px-5 py-4 flex items-center justify-between bg-primary-100/30">
              <span className="text-[13px] font-semibold text-neutral-900">= Your commission</span>
              <span className="text-[22px] font-bold font-mono tabular-nums text-neutral-900">${t.commission.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
          </div>
        </Card>
      </Section>

      {overdue90.length > 0 && (
        <Banner tone="warn" icon="warn" title={`${overdue90.length} invoice${overdue90.length === 1 ? '' : 's'} overdue more than 90 days — needs attention`}>
          <Money value={overdueTotal} className="font-semibold"/> outstanding. These are flagged for escalation; contact the client or your manager.
        </Banner>
      )}

      {/* Carryover callout — only shown when the rep has carryovers this period */}
      {(() => {
        const carryovers = repInvoices.filter(i => i.isCarryover);
        if (carryovers.length === 0) return null;
        const total = carryovers.reduce((a, i) => a + Number(i.commission || 0), 0);
        return (
          <Banner tone="success" icon="check" title={`${carryovers.length} carryover${carryovers.length === 1 ? '' : 's'} released this period — $${total.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`}>
            These are commissions from earlier months that were held while we waited for the customer to pay. They cleared in this period's AR, so they're paid out now.
          </Banner>
        );
      })()}

      <Section title="Your invoices this month" sub={`${repInvoices.length} invoices`}
        right={<div className="flex items-center gap-2"><Input icon="search" value="" onChange={()=>{}} placeholder="Search invoices…" className="w-56"/>
          <ExportMenu rep={effRep} invoices={invoices} period={periodLabel} tenant={tenant}/></div>}>

        {/* Bulk-action toolbar — only when admins are in preview mode AND
            something is selected. Lets the admin exclude/include multiple
            invoices in one shot (e.g. strip out negative-commission
            carryovers). */}
        {canEditInvoices && selectedIds.size > 0 && (
          <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-primary-100/40 border border-primary-200 rounded-md">
            <div className="text-[13px] text-primary-900">
              <b>{selectedIds.size}</b> invoice{selectedIds.size === 1 ? '' : 's'} selected
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" onClick={clearSelected} disabled={bulkBusy}>Clear</Button>
              <Button size="sm" variant="secondary" icon="check" onClick={() => bulkSetExcluded(false)} disabled={bulkBusy}>
                {bulkBusy ? 'Working…' : 'Include selected'}
              </Button>
              <Button size="sm" variant="destructive" icon="x" onClick={() => bulkSetExcluded(true)} disabled={bulkBusy}>
                {bulkBusy ? 'Working…' : 'Exclude selected'}
              </Button>
            </div>
          </div>
        )}

        <Card padding="p-0">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-neutral-500 bg-canvas/60 border-b border-neutral-200">
                {canEditInvoices && (
                  <th className="px-3 py-2.5 font-semibold w-8">
                    <input type="checkbox"
                      checked={selectedIds.size > 0 && selectedIds.size === repInvoices.length}
                      ref={el => { if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < repInvoices.length; }}
                      onChange={() => {
                        if (selectedIds.size === repInvoices.length) clearSelected();
                        else setSelectedIds(new Set(repInvoices.map(i => i.id)));
                      }}
                      className="w-4 h-4 accent-primary-700"/>
                  </th>
                )}
                <th className="px-4 py-2.5 font-semibold">Client</th>
                <th className="px-4 py-2.5 font-semibold">Invoice</th>
                <th className="px-4 py-2.5 font-semibold">Date</th>
                <th className="px-4 py-2.5 font-semibold text-right">Total Bill</th>
                <th className="px-4 py-2.5 font-semibold text-right">GP</th>
                <th className="px-4 py-2.5 font-semibold text-center">Rate</th>
                <th className="px-4 py-2.5 font-semibold text-right">Commission</th>
                <th className="px-4 py-2.5 font-semibold text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200">
              {repInvoices.map(inv => (
                <React.Fragment key={inv.id}>
                  <tr className={`hover:bg-primary-100/30 cursor-pointer ${selectedIds.has(inv.id) ? 'bg-primary-100/40' : inv.rejected ? 'bg-red-50/40' : inv.isCarryover ? 'bg-blue-50/30' : ''}`} onClick={() => onOpenInvoice(enrich(inv))}>
                    {canEditInvoices && (
                      <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                        <input type="checkbox" checked={selectedIds.has(inv.id)}
                          onChange={() => toggleSelected(inv.id)}
                          className="w-4 h-4 accent-primary-700"/>
                      </td>
                    )}
                    <td className="px-4 py-3 font-medium text-neutral-900">{inv.client}</td>
                    <td className="px-4 py-3 font-mono text-neutral-700">
                      {inv.doc}
                      {inv.isCarryover && <Pill tone="info" className="ml-2 normal-case tracking-normal">carryover</Pill>}
                    </td>
                    <td className="px-4 py-3 font-mono text-neutral-500">{inv.date}</td>
                    <td className="px-4 py-3 text-right"><Money value={inv.totalBill}/></td>
                    <td className="px-4 py-3 text-right"><Money value={inv.gp}/></td>
                    <td className="px-4 py-3 text-center"><Pill tone={inv.tier === 'y1' ? 'y1' : 'y2'}>{inv.tier === 'y1' ? 'Y1' : 'Y2+'}</Pill></td>
                    <td className="px-4 py-3 text-right">
                      {inv.rejected ? (
                        <span>
                          <span className="text-[11px] font-mono text-neutral-400 line-through mr-1.5">${(inv.preRejectionCommission ?? 0).toFixed(2)}</span>
                          <Money value={0} className="font-semibold text-red-700"/>
                        </span>
                      ) : inv.heldForCollection ? (
                        <span>
                          <span className="text-[11px] font-mono text-neutral-400 line-through mr-1.5">${Number(inv.heldWouldBeCommission || 0).toFixed(2)}</span>
                          <Money value={0} className="font-semibold text-amber-700"/>
                        </span>
                      ) : (
                        <Money value={inv.commission} className="font-semibold text-neutral-900"/>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {inv.excluded ? <Pill tone="neutral">excluded</Pill>
                        : inv.rejected ? <Pill tone="error">rejected</Pill>
                        : inv.heldForCollection ? <Pill tone="warn">held — on collection</Pill>
                        : inv.paid ? <Pill tone="paid">paid</Pill>
                        : inv.ageDays > 90 ? <Pill tone="unpaid">{inv.ageDays}d · 90+</Pill>
                        : inv.ageDays > 30 ? <Pill tone="warn">{inv.ageDays}d · carry</Pill>
                        : <Pill tone="unpaid">{inv.ageDays}d</Pill>}
                    </td>
                  </tr>
                  {inv.rejected && inv.rejectionReason && (
                    <tr className="bg-red-50/40">
                      <td colSpan={canEditInvoices ? 9 : 8} className="px-4 pb-3 -mt-1 text-[12px] text-red-900">
                        <span className="font-semibold">Rejection reason:</span> {inv.rejectionReason}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </Card>
      </Section>
    </div>
  );
}

export function BreakdownRow({ label, value, sub, bold, rate }) {
  return (
    <div className={`flex items-center justify-between px-5 py-3 ${bold ? 'bg-canvas/40' : ''}`}>
      <span className={`text-[13px] ${bold ? 'font-semibold text-neutral-900' : sub ? 'text-neutral-600 pl-4' : 'text-neutral-800'}`}>{label}</span>
      {rate ? <Pill tone="brand">applied per invoice</Pill> :
        <span className={`font-mono tabular-nums ${bold ? 'text-[15px] font-semibold text-neutral-900' : 'text-[14px] text-neutral-800'}`}>
          {value < 0 ? <span className="text-red-700">−${Math.abs(value).toFixed(2)}</span> : `$${value.toFixed(2)}`}
        </span>}
    </div>
  );
}

// ===================== S17 · History =====================
// All runs the rep participated in, with per-run aggregates.
// Invoices are RLS-filtered to the signed-in rep, so we can safely pull
// every invoice they've ever had and group client-side.
export function ScreenRepHistory({ rep }) {
  const [runs, setRuns] = useStateRep([]);
  const [byRun, setByRun] = useStateRep({});
  const [loading, setLoading] = useStateRep(true);

  useEffectRep(() => {
    let alive = true;
    (async () => {
      try {
        const [allRunsRaw, allInvs] = await Promise.all([
          api.runs.list(),
          api.invoices.listAllForCurrentRep(),
        ]);
        if (!alive) return;
        // Same rep-visibility filter as the main dashboard — hide
        // import-only runs.
        const allRuns = allRunsRaw.filter(r => r.visibleToReps !== false);
        // Group invoices by run_id.
        const grouped = {};
        allInvs.forEach(i => { (grouped[i.runId] ||= []).push(adaptInvoice(i)); });
        setRuns(allRuns);
        setByRun(grouped);
      } catch (err) { console.error('Rep history load:', err); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) return <div className="p-10 text-center text-neutral-500 text-[13px]">Loading your history…</div>;

  const runRows = runs
    .map(r => ({ run: r, totals: summarize(byRun[r.id] || []) }))
    .filter(r => r.totals.count > 0);

  if (runRows.length === 0) {
    return <Banner tone="info" icon="info" title="No runs yet">Once your first run is finalized, your history will appear here.</Banner>;
  }

  const totalAll = runRows.reduce((a, r) => a + r.totals.commission, 0);
  const avg = totalAll / runRows.length;
  const last = runRows[0]?.totals.commission || 0;
  const prev = runRows[1]?.totals.commission || 0;
  const trend = prev > 0 ? ((last - prev) / prev) * 100 : null;
  const max = Math.max(...runRows.map(r => r.totals.commission));

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-[24px] font-bold text-neutral-900 tracking-[-0.01em]">Your history</h1>
          <div className="text-[13px] text-neutral-500 mt-1">Commission earned over the last 12 months</div>
        </div>
        <div className="flex gap-2">
          <Select value="2026" onChange={()=>{}} options={[{value:'2026',label:'Year to date'},{value:'2025',label:'2025'}]}/>
          <Select value="12" onChange={()=>{}} options={[{value:'12',label:'12 months'},{value:'6',label:'6 months'},{value:'3',label:'3 months'}]}/>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card padding="p-5">
          <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500">Total earned</div>
          <div className="mt-1 text-[22px] font-bold font-mono tabular-nums">${totalAll.toLocaleString('en-US', {maximumFractionDigits:0})}</div>
          <div className="text-[12px] text-neutral-500 mt-1">Across {runRows.length} run{runRows.length === 1 ? '' : 's'}</div>
        </Card>
        <Card padding="p-5">
          <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500">Run average</div>
          <div className="mt-1 text-[22px] font-bold font-mono tabular-nums">${avg.toLocaleString('en-US', {maximumFractionDigits:0})}</div>
          <div className="text-[12px] text-neutral-500 mt-1">Per month</div>
        </Card>
        <Card padding="p-5">
          <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500">Month over month</div>
          {trend == null
            ? <div className="mt-1 text-[22px] font-bold font-mono tabular-nums text-neutral-400">—</div>
            : <div className={`mt-1 text-[22px] font-bold font-mono tabular-nums ${trend >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{trend >= 0 ? '▲' : '▼'} {Math.abs(trend).toFixed(1)}%</div>}
          <div className="text-[12px] text-neutral-500 mt-1">Latest vs previous</div>
        </Card>
      </div>

      {runRows.length > 1 && (
        <Section title="Monthly commission">
          <Card padding="p-5">
            <div className="flex items-end gap-2 h-[220px]">
              {[...runRows].reverse().map((r, i) => (
                <div key={r.run.id} className="flex-1 flex flex-col items-center gap-2 group cursor-pointer">
                  <div className="flex-1 w-full flex items-end">
                    <div className={`w-full rounded-t ${i === runRows.length - 1 ? 'bg-primary-700' : 'bg-primary-900/70 group-hover:bg-primary-700'} transition-all`}
                      style={{ height: `${(r.totals.commission / max) * 100}%` }}/>
                  </div>
                  <div className="text-[10px] text-neutral-500 font-medium">{r.run.period.slice(0, 3)}</div>
                  <div className="text-[11px] font-mono text-neutral-700">${(r.totals.commission/1000).toFixed(1)}k</div>
                </div>
              ))}
            </div>
          </Card>
        </Section>
      )}

      <Section title="Periods">
        <Card padding="p-0">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-neutral-500 bg-canvas/60 border-b border-neutral-200">
                <th className="px-4 py-2.5 font-semibold">Period</th>
                <th className="px-4 py-2.5 font-semibold">Status</th>
                <th className="px-4 py-2.5 font-semibold text-right">Invoices</th>
                <th className="px-4 py-2.5 font-semibold text-right">Gross Profit</th>
                <th className="px-4 py-2.5 font-semibold text-right">Commission</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200">
              {runRows.map(({ run, totals }) => (
                <tr key={run.id} className="hover:bg-primary-100/30 cursor-pointer">
                  <td className="px-4 py-3 font-semibold text-neutral-900">{run.period}</td>
                  <td className="px-4 py-3"><Pill tone={run.status === 'finalized' ? 'finalized' : 'draft'}>{run.status}</Pill></td>
                  <td className="px-4 py-3 text-right font-mono">{totals.count}</td>
                  <td className="px-4 py-3 text-right"><Money value={totals.gp}/></td>
                  <td className="px-4 py-3 text-right"><Money value={totals.commission} className="font-semibold"/></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </Section>
    </div>
  );
}

// ===================== S18 · Unpaid =====================
export function ScreenRepUnpaid({ rep, onOpenInvoice }) {
  const [unpaid, setUnpaid] = useStateRep([]);
  const [loading, setLoading] = useStateRep(true);

  useEffectRep(() => {
    let alive = true;
    (async () => {
      try {
        const allInvs = await api.invoices.listAllForCurrentRep();
        if (!alive) return;
        const rows = allInvs.filter(i => !i.paid).map(adaptInvoice).sort((a, b) => b.ageDays - a.ageDays);
        setUnpaid(rows);
      } catch (err) { console.error('Unpaid load:', err); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) return <div className="p-10 text-center text-neutral-500 text-[13px]">Loading unpaid invoices…</div>;

  const total = sumBy(unpaid, i => i.outstanding);
  const buckets = [
    { label: '0–30',   min: 0,   max: 30,  tone: 'bg-emerald-500' },
    { label: '31–60',  min: 31,  max: 60,  tone: 'bg-sky-500'     },
    { label: '61–90',  min: 61,  max: 90,  tone: 'bg-amber-500'   },
    { label: '91–120', min: 91,  max: 120, tone: 'bg-orange-500'  },
    { label: '121+',   min: 121, max: 9999,tone: 'bg-red-600'     },
  ];
  const bucketTotals = buckets.map(b => sumBy(unpaid.filter(i => i.ageDays >= b.min && i.ageDays <= b.max), i => i.outstanding));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-[24px] font-bold text-neutral-900 tracking-[-0.01em]">Unpaid invoices</h1>
        <div className="text-[13px] text-neutral-500 mt-1">{unpaid.length} outstanding · <Money value={total} className="font-semibold text-neutral-900"/> total</div>
      </div>

      {unpaid.length === 0 ? (
        <Banner tone="success" icon="check" title="All your invoices are current">
          Nothing overdue. Keep it up.
        </Banner>
      ) : (
        <>
          <Section title="Aging">
            <Card padding="p-4">
              <div className="flex gap-1 rounded-md overflow-hidden h-12 mb-3">
                {buckets.map((b, i) => (
                  <div key={b.label} className={`${b.tone} flex-1 relative group cursor-pointer hover:opacity-90 transition`}
                    style={{ flex: `${Math.max(bucketTotals[i], 100)} 0 0` }}>
                    <div className="absolute inset-0 flex items-center justify-center text-white text-[11px] font-semibold">
                      {bucketTotals[i] > 0 && `$${(bucketTotals[i] / 1000).toFixed(1)}k`}
                    </div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-5 gap-1 text-center">
                {buckets.map((b, i) => (
                  <div key={b.label}>
                    <div className="text-[11px] text-neutral-500 font-semibold">{b.label} days</div>
                    <div className="text-[13px] font-mono font-semibold text-neutral-900">${bucketTotals[i].toLocaleString('en-US',{maximumFractionDigits:0})}</div>
                  </div>
                ))}
              </div>
            </Card>
          </Section>

          <Section title="Invoices, oldest first">
            <Card padding="p-0">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-neutral-500 bg-canvas/60 border-b border-neutral-200">
                    <th className="px-4 py-2.5 font-semibold">Client</th>
                    <th className="px-4 py-2.5 font-semibold">Invoice</th>
                    <th className="px-4 py-2.5 font-semibold">Date</th>
                    <th className="px-4 py-2.5 font-semibold text-right">Age</th>
                    <th className="px-4 py-2.5 font-semibold text-right">Outstanding</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-200">
                  {unpaid.map(inv => {
                    const b = buckets.find(b => inv.ageDays >= b.min && inv.ageDays <= b.max);
                    return (
                      <tr key={inv.id} className="hover:bg-primary-100/30 cursor-pointer" onClick={() => onOpenInvoice(enrich(inv))}>
                        <td className="px-4 py-3 font-medium text-neutral-900">{inv.client}</td>
                        <td className="px-4 py-3 font-mono text-neutral-700">{inv.doc}</td>
                        <td className="px-4 py-3 font-mono text-neutral-500">{inv.date}</td>
                        <td className="px-4 py-3 text-right">
                          <span className={`inline-flex items-center gap-1.5 font-mono ${inv.ageDays > 90 ? 'text-red-700 font-semibold' : inv.ageDays > 60 ? 'text-amber-700' : 'text-neutral-700'}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${b.tone}`}/>{inv.ageDays}d
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right"><Money value={inv.outstanding} className="font-semibold"/></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          </Section>
        </>
      )}
    </div>
  );
}

